import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useActivityTypeMutations,
  useActivityTypes,
  useCustomFieldMutations,
  useCustomFields,
  useInvites,
  useMembers,
  useStageMutations,
  useStages,
  useTagMutations,
  useTags,
} from '../api/queries';
import type { ActivityType, CustomField, FieldType, Org, Stage } from '@figureit/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Single-select' },
  { value: 'url', label: 'URL' },
  { value: 'email', label: 'Email' },
];

export function SettingsView() {
  return (
    <div className="p-3 md:p-6 max-w-4xl overflow-y-auto overscroll-contain h-full">
      <OrgSection />
      <GoalsSection />
      <CloseReasonsSection />
      <NotificationPrefsSection />
      <MembersSection />
      <CustomFieldsSection />
      <StagesSection />
      <ActivityTypesSection />
      <TagsSection />
      <DangerZone />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-8">
      <h2 className="text-base font-bold mb-3 pb-2 border-b border-border">{title}</h2>
      {children}
    </div>
  );
}

function OrgSection() {
  const { me, currentOrgId } = useAuth();
  const org = me?.organizations.find((o) => o.id === currentOrgId);
  return (
    <Section title="Organization">
      <div className="bg-surface border border-border rounded-md p-4 text-sm">
        <div>
          <span className="text-text3">Name:</span> <strong>{org?.name ?? '—'}</strong>
        </div>
        <div>
          <span className="text-text3">Slug:</span> {org?.slug ?? '—'}
        </div>
        <div>
          <span className="text-text3">Your role:</span> {org?.role ?? '—'}
        </div>
      </div>
    </Section>
  );
}

function GoalsSection() {
  const qc = useQueryClient();
  const { data: org } = useQuery({
    queryKey: ['org'],
    queryFn: () => api<Org>('/api/org'),
  });
  const update = useMutation({
    mutationFn: (body: Partial<Org>) =>
      api('/api/org', { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org'] });
      qc.invalidateQueries({ queryKey: ['report'] });
    },
  });
  const [deals, setDeals] = useState<string>('');
  const [revenue, setRevenue] = useState<string>('');
  useEffect(() => {
    if (org) {
      setDeals(org.quarterlyGoalDeals == null ? '' : String(org.quarterlyGoalDeals));
      setRevenue(org.quarterlyGoalRevenue == null ? '' : String(org.quarterlyGoalRevenue));
    }
  }, [org]);
  const save = () =>
    update.mutate({
      quarterlyGoalDeals: deals === '' ? null : Number(deals),
      quarterlyGoalRevenue: revenue === '' ? null : Number(revenue),
    } as Partial<Org>);
  return (
    <Section title="Quarterly goals">
      <p className="text-xs text-text3 mb-3">
        Shown as progress bars on Overview and Reports. Leave blank to disable.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
        <label className="block">
          <span className="text-[11px] text-text3 block mb-1">Deals to win</span>
          <input
            type="number"
            inputMode="numeric"
            value={deals}
            onChange={(e) => setDeals(e.target.value)}
            placeholder="e.g. 10"
          />
        </label>
        <label className="block">
          <span className="text-[11px] text-text3 block mb-1">Revenue (₪/yr)</span>
          <input
            type="number"
            inputMode="numeric"
            value={revenue}
            onChange={(e) => setRevenue(e.target.value)}
            placeholder="e.g. 1500000"
          />
        </label>
      </div>
      <button className="btn btn-sm btn-primary mt-3" onClick={save} disabled={update.isPending}>
        {update.isPending ? 'Saving…' : 'Save goals'}
      </button>
    </Section>
  );
}

function CloseReasonsSection() {
  const qc = useQueryClient();
  const { data: org } = useQuery({
    queryKey: ['org'],
    queryFn: () => api<Org>('/api/org'),
  });
  const update = useMutation({
    mutationFn: (body: Partial<Org>) =>
      api('/api/org', { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org'] }),
  });
  const [won, setWon] = useState<string>('');
  const [lost, setLost] = useState<string>('');
  useEffect(() => {
    if (org) {
      setWon((org.wonReasons ?? []).join('\n'));
      setLost((org.lostReasons ?? []).join('\n'));
    }
  }, [org]);
  const save = () =>
    update.mutate({
      wonReasons: won
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
      lostReasons: lost
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
    } as Partial<Org>);
  return (
    <Section title="Close reasons">
      <p className="text-xs text-text3 mb-3">
        Reps pick from these when closing a deal as Won/Lost. Reports use them to break down what's
        working and what isn't. One per line.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] text-text3 block mb-1">Won reasons</span>
          <textarea rows={6} value={won} onChange={(e) => setWon(e.target.value)} />
        </label>
        <label className="block">
          <span className="text-[11px] text-text3 block mb-1">Lost reasons</span>
          <textarea rows={6} value={lost} onChange={(e) => setLost(e.target.value)} />
        </label>
      </div>
      <button className="btn btn-sm btn-primary mt-3" onClick={save} disabled={update.isPending}>
        {update.isPending ? 'Saving…' : 'Save reasons'}
      </button>
    </Section>
  );
}

function NotificationPrefsSection() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['notif-prefs'],
    queryFn: () =>
      api<{
        notifyDailyDigest: boolean;
        notifyOnAssign: boolean;
        notifyOnStageChange: boolean;
      }>('/api/me/notifications'),
  });
  const update = useMutation({
    mutationFn: (body: Partial<NonNullable<typeof data>>) =>
      api('/api/me/notifications', { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notif-prefs'] }),
  });
  const previewDigest = useMutation({
    mutationFn: () => api<{ items: number }>('/api/me/digest/send', { method: 'POST' }),
  });
  if (!data) return null;
  const Toggle = ({
    label,
    desc,
    checked,
    onChange,
  }: {
    label: string;
    desc: string;
    checked: boolean;
    onChange: (v: boolean) => void;
  }) => (
    <label className="flex items-start gap-3 bg-surface2 rounded-md p-3 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-auto mt-0.5"
      />
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-text3">{desc}</div>
      </div>
    </label>
  );
  return (
    <Section title="My notifications">
      <div className="space-y-2 mb-3">
        <Toggle
          label="Daily digest email"
          desc="Each weekday morning, a summary of your overdue and due-today actions."
          checked={data.notifyDailyDigest}
          onChange={(v) => update.mutate({ notifyDailyDigest: v })}
        />
        <Toggle
          label="When a lead is assigned to me"
          desc="Email me when a teammate assigns me as the owner of a lead."
          checked={data.notifyOnAssign}
          onChange={(v) => update.mutate({ notifyOnAssign: v })}
        />
        <Toggle
          label="When my leads change stage"
          desc="Off by default — turn on if you want to track every move someone else makes."
          checked={data.notifyOnStageChange}
          onChange={(v) => update.mutate({ notifyOnStageChange: v })}
        />
      </div>
      <button
        className="btn btn-sm btn-secondary"
        onClick={() =>
          previewDigest.mutate(undefined, {
            onSuccess: (res) =>
              alert(
                res.items === 0
                  ? "You're all caught up — nothing to digest."
                  : `Sent a preview digest with ${res.items} item(s) to your inbox.`,
              ),
          })
        }
      >
        Send me a digest preview now
      </button>
    </Section>
  );
}

function MembersSection() {
  const { me, currentOrgId } = useAuth();
  const myRole = me?.organizations.find((o) => o.id === currentOrgId)?.role;
  const isPrivileged = myRole === 'owner' || myRole === 'admin';

  const membersQ = useMembers();
  const invitesQ = useInvites();
  const qc = useQueryClient();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'admin' | 'owner'>('member');

  const invite = useMutation({
    mutationFn: (body: { email: string; role: string }) =>
      api('/api/org/invites', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      setEmail('');
      qc.invalidateQueries({ queryKey: ['invites'] });
    },
  });
  const updateRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      api(`/api/org/members/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
  });
  const removeMember = useMutation({
    mutationFn: (userId: string) => api(`/api/org/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
  });

  return (
    <Section title="Members & invites">
      <div className="space-y-2 mb-4">
        {(membersQ.data?.items ?? []).map((m) => (
          <div key={m.id} className="bg-surface2 rounded-md p-3 flex flex-wrap items-center gap-3">
            {m.avatarUrl ? (
              <img src={m.avatarUrl} alt="" className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-white text-xs font-bold">
                {m.email[0]?.toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{m.name ?? m.email}</div>
              <div className="text-xs text-text3 truncate">{m.email}</div>
            </div>
            {isPrivileged ? (
              <select
                value={m.role}
                onChange={(e) => updateRole.mutate({ userId: m.userId, role: e.target.value })}
                className="w-auto max-w-[140px] text-xs"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
                {myRole === 'owner' && <option value="owner">Owner</option>}
              </select>
            ) : (
              <span className="text-xs text-text3">{m.role}</span>
            )}
            {isPrivileged && m.userId !== me?.id && (
              <button
                className="btn btn-sm btn-danger"
                onClick={() => {
                  if (confirm(`Remove ${m.email} from the organization?`)) {
                    removeMember.mutate(m.userId);
                  }
                }}
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>

      {isPrivileged && (
        <>
          <h3 className="text-sm text-text2 mb-2 font-semibold">Invite a teammate</h3>
          <div className="flex flex-wrap gap-2 mb-3">
            <input
              type="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              placeholder="email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full sm:w-auto sm:max-w-xs"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as typeof role)}
              className="flex-1 sm:flex-none sm:max-w-[120px]"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
              {myRole === 'owner' && <option value="owner">Owner</option>}
            </select>
            <button
              className="btn btn-primary"
              disabled={!email}
              onClick={() => invite.mutate({ email, role })}
            >
              Send invite
            </button>
          </div>
          <div className="text-xs text-text3">
            Pending invites: {(invitesQ.data?.items ?? []).map((i) => i.email).join(', ') || 'none'}
          </div>
        </>
      )}
    </Section>
  );
}

function CustomFieldsSection() {
  const { data } = useCustomFields();
  const m = useCustomFieldMutations();
  const items = (data?.items ?? []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return (
    <Section title="Custom fields">
      <p className="text-xs text-text3 mb-2">
        Add columns visible across the list view and lead detail panel.
      </p>
      <div className="flex flex-col gap-1.5">
        {items.map((f) => (
          <CustomFieldRow
            key={f.id}
            field={f}
            onUpdate={m.update.mutate}
            onRemove={m.remove.mutate}
          />
        ))}
      </div>
      <button
        className="btn btn-sm btn-secondary mt-2"
        onClick={() =>
          m.create.mutate({
            key: `field_${Date.now()}`,
            label: 'New field',
            type: 'text',
            options: [],
            showInList: true,
            order: items.length,
          })
        }
      >
        + Add field
      </button>
    </Section>
  );
}

function CustomFieldRow({
  field,
  onUpdate,
  onRemove,
}: {
  field: CustomField;
  onUpdate: (cf: Partial<CustomField> & { id: string }) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="grid grid-cols-[24px_1fr_auto] md:grid-cols-[24px_1fr_140px_2fr_auto] gap-2 items-center bg-surface2 rounded-md px-3 py-2">
      <span className="text-text3 text-xs">{(field.order ?? 0) + 1}</span>
      <input
        defaultValue={field.label}
        onBlur={(e) => onUpdate({ id: field.id, label: e.target.value })}
        className="text-xs"
      />
      <select
        defaultValue={field.type}
        onChange={(e) => onUpdate({ id: field.id, type: e.target.value as FieldType })}
        className="text-xs col-start-2 md:col-start-auto"
      >
        {FIELD_TYPES.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </select>
      {field.type === 'select' ? (
        <input
          defaultValue={(field.options ?? []).join(', ')}
          onBlur={(e) =>
            onUpdate({
              id: field.id,
              options: e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          placeholder="Comma-separated options"
          className="text-xs col-start-2 md:col-start-auto"
        />
      ) : (
        <span className="text-xs text-text3 col-start-2 md:col-start-auto">
          {field.type === 'url' && 'Clickable URL'}
          {field.type === 'email' && 'Mailto link'}
          {field.type === 'number' && 'Numeric input'}
          {field.type === 'date' && 'Date picker'}
          {field.type === 'text' && 'Free text'}
        </span>
      )}
      <div className="flex items-center gap-1.5 col-start-3 row-start-1 md:col-start-auto md:row-start-auto">
        <label className="flex items-center gap-1 text-[11px] text-text3">
          <input
            type="checkbox"
            defaultChecked={field.showInList !== false}
            onChange={(e) => onUpdate({ id: field.id, showInList: e.target.checked })}
            className="w-auto"
          />
          List
        </label>
        <button
          className="btn btn-sm btn-danger"
          onClick={() => {
            if (confirm('Remove this field?')) onRemove(field.id);
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

function StagesSection() {
  const { data } = useStages();
  const m = useStageMutations();
  const stages = (data?.items ?? []).slice().sort((a, b) => a.order - b.order);
  return (
    <Section title="Pipeline stages">
      <p className="text-xs text-text3 mb-2">
        <strong>Probability</strong> drives the weighted forecast on Reports.{' '}
        <strong>SLA days</strong> turns a Kanban card red when a lead has been parked too long in
        this stage.
      </p>
      <div className="flex flex-col gap-1.5">
        {stages.map((s, i) => (
          <StageRow
            key={s.id}
            stage={s}
            index={i}
            onUpdate={m.update.mutate}
            onRemove={m.remove.mutate}
          />
        ))}
      </div>
      <button
        className="btn btn-sm btn-secondary mt-2"
        onClick={() =>
          m.create.mutate({
            name: 'New Stage',
            order: stages.length,
            color: '#6366f1',
            probability: 0,
          })
        }
      >
        + Add stage
      </button>
    </Section>
  );
}

function StageRow({
  stage,
  index,
  onUpdate,
  onRemove,
}: {
  stage: Stage;
  index: number;
  onUpdate: (s: Partial<Stage> & { id: string }) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="grid grid-cols-[24px_44px_1fr] md:grid-cols-[24px_36px_1fr_84px_84px_auto] items-center gap-2 bg-surface2 rounded-md px-3 py-2">
      <span className="text-text3 text-xs text-center">{index + 1}</span>
      <input
        type="color"
        defaultValue={stage.color}
        onBlur={(e) => onUpdate({ id: stage.id, color: e.target.value })}
        className="w-11 md:w-9 h-11 md:h-8 !min-h-0 p-0.5 cursor-pointer"
      />
      <input
        defaultValue={stage.name}
        onBlur={(e) => onUpdate({ id: stage.id, name: e.target.value })}
        className="min-w-0"
      />
      <label className="col-start-3 md:col-start-auto flex items-center gap-2 md:block">
        <span className="md:hidden text-[11px] text-text3 w-16 flex-shrink-0">Prob. %</span>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          max={100}
          defaultValue={stage.probability}
          title="Probability of close-won, %"
          onBlur={(e) =>
            onUpdate({
              id: stage.id,
              probability: Math.max(0, Math.min(100, Number(e.target.value) || 0)),
            })
          }
          className="text-xs"
        />
      </label>
      <label className="col-start-3 md:col-start-auto flex items-center gap-2 md:block">
        <span className="md:hidden text-[11px] text-text3 w-16 flex-shrink-0">SLA days</span>
        <input
          type="number"
          inputMode="numeric"
          min={1}
          defaultValue={stage.slaDays ?? ''}
          title="SLA days (red flag if a lead sits longer than this)"
          placeholder="—"
          onBlur={(e) =>
            onUpdate({
              id: stage.id,
              slaDays: e.target.value === '' ? null : Number(e.target.value),
            })
          }
          className="text-xs"
        />
      </label>
      <div className="flex items-center gap-2 md:gap-1 col-start-3 md:col-start-auto">
        <label className="text-xs text-text3 flex items-center gap-1">
          <input
            type="checkbox"
            defaultChecked={stage.isWon}
            onChange={(e) => onUpdate({ id: stage.id, isWon: e.target.checked })}
            className="w-auto"
          />
          Won
        </label>
        <label className="text-xs text-text3 flex items-center gap-1">
          <input
            type="checkbox"
            defaultChecked={stage.isLost}
            onChange={(e) => onUpdate({ id: stage.id, isLost: e.target.checked })}
            className="w-auto"
          />
          Lost
        </label>
        <button
          className="btn btn-sm btn-danger"
          onClick={() => {
            if (confirm('Remove stage?')) onRemove(stage.id);
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

function ActivityTypesSection() {
  const { data } = useActivityTypes();
  const m = useActivityTypeMutations();
  const [name, setName] = useState('');
  return (
    <Section title="Activity types">
      <p className="text-xs text-text3 mb-2">
        Set a <strong>template</strong> per type to prefill the activity summary — reps log
        calls/meetings faster and consistently.
      </p>
      <div className="flex flex-col gap-1.5 mb-3">
        {(data?.items ?? []).map((t) => (
          <ActivityTypeRow
            key={t.id}
            type={t}
            onUpdate={m.update.mutate}
            onRemove={m.remove.mutate}
          />
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New activity type…"
          className="flex-1 sm:flex-none sm:max-w-[200px]"
          enterKeyHint="done"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && name.trim()) {
              m.create.mutate(name.trim());
              setName('');
            }
          }}
        />
        <button
          className="btn btn-secondary"
          onClick={() => {
            if (name.trim()) {
              m.create.mutate(name.trim());
              setName('');
            }
          }}
        >
          Add
        </button>
      </div>
    </Section>
  );
}

function ActivityTypeRow({
  type,
  onUpdate,
  onRemove,
}: {
  type: ActivityType;
  onUpdate: (input: { id: string; name?: string; template?: string | null }) => void;
  onRemove: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-surface2 rounded-md px-3 py-2">
      <div className="flex items-center gap-2">
        <input
          defaultValue={type.name}
          onBlur={(e) => onUpdate({ id: type.id, name: e.target.value })}
          className="text-sm flex-1"
        />
        <button className="btn btn-sm btn-ghost text-xs" onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide template' : type.template ? 'Edit template' : '+ Template'}
        </button>
        <button
          className="text-text3 hover:text-red text-lg px-2 min-h-[40px]"
          aria-label="Remove activity type"
          onClick={() => {
            if (confirm(`Remove "${type.name}"?`)) onRemove(type.id);
          }}
        >
          ×
        </button>
      </div>
      {open && (
        <div className="mt-2">
          <textarea
            rows={4}
            defaultValue={type.template ?? ''}
            placeholder="e.g. Spoke with {contact}. Topics:\n- \nNext step: "
            onBlur={(e) => onUpdate({ id: type.id, template: e.target.value || null })}
          />
          <div className="text-[11px] text-text3 mt-1">
            Tokens: <code>{`{contact}`}</code>, <code>{`{date}`}</code>
          </div>
        </div>
      )}
    </div>
  );
}

function TagsSection() {
  const { data } = useTags();
  const m = useTagMutations();
  const [name, setName] = useState('');
  const [color, setColor] = useState('#6366f1');
  return (
    <Section title="Tags">
      <div className="flex flex-wrap gap-2 mb-3">
        {(data?.items ?? []).map((t) => (
          <span
            key={t.id}
            className="border border-border rounded-md px-3 py-1 text-sm flex items-center gap-2"
            style={{ background: `${t.color}22`, color: t.color }}
          >
            {t.name}
            <button
              onClick={() => {
                if (confirm(`Remove tag "${t.name}"?`)) m.remove.mutate(t.id);
              }}
              className="hover:text-red px-1 -mr-1 text-base leading-none"
              aria-label={`Remove tag ${t.name}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2 items-center">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New tag"
          className="flex-1 sm:flex-none sm:max-w-[200px] min-w-0"
          enterKeyHint="done"
        />
        <input
          type="color"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          className="w-11 md:w-9 h-11 md:h-8 !min-h-0 p-0.5 flex-shrink-0"
        />
        <button
          className="btn btn-secondary"
          onClick={() => {
            if (name.trim()) {
              m.create.mutate({ name: name.trim(), color });
              setName('');
            }
          }}
        >
          Add
        </button>
      </div>
    </Section>
  );
}

function DangerZone() {
  return (
    <Section title="Danger zone">
      <p className="text-xs text-text3 mb-2">
        Backups can be downloaded from the Reports tab. Account deletion is currently a manual
        operation — contact an admin.
      </p>
    </Section>
  );
}
